import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { secureStorage } from './secure-storage.service';
import { CookieService } from './cookie.service';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private refreshTimer: any = null;
  private tokenGenerationInProgress = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private localStorage: secureStorage,
    private cookieService: CookieService
  ) { }

  // Get access token from localStorage
  getAccessToken(): string | null {
    return this.localStorage.getItem('eol_access_token');
  }

  // Get refresh token from localStorage
  getRefreshToken(): string | null {
    return this.localStorage.getItem('eol_ref_token');
  }

  // Get user code from localStorage
  getUserCode(): string | null {
    return this.localStorage.getItem('eol_user_code');
  }

  // Set access token in localStorage
  setAccessToken(token: string): void {
    this.localStorage.setItem('eol_access_token', token);
  }

  // Set refresh token in localStorage
  setRefreshToken(token: string): void {
    this.localStorage.setItem('eol_ref_token', token);
  }

  // Set user code in localStorage
  setUserCode(userCode: string): void {
    this.localStorage.setItem('eol_user_code', userCode);
  }

  // Check if user is authenticated (has both token and user code)
  isAuthenticated(): boolean {
    return !!(this.getAccessToken() && this.getUserCode());
  }

  // Check if user has valid and complete authentication
  hasValidAuthentication(): boolean {
    const accessToken = this.getAccessToken();
    const userCode = this.getUserCode();
    return !!(accessToken && userCode && !this.isTokenExpired());
  }

  // Check if user has access token (for free search scenarios)
  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  // Check if token needs to be generated (for non-authenticated users)
  needsTokenGeneration(): boolean {
    const accessToken = this.getAccessToken();
    const userCode = this.getUserCode();

    // If user is authenticated and has valid token, no action needed
    if (accessToken && userCode && !this.isTokenExpired()) {
      return false;
    }

    // If user is authenticated but token is expired or missing
    if (userCode && (!accessToken || this.isTokenExpired())) {
      return true;
    }

    // If no authentication at all, generate free search token
    if (!accessToken && !userCode) {
      return true;
    }

    // If has token but no user code (free search scenario) and token is expired
    if (accessToken && !userCode && this.isTokenExpired()) {
      return true;
    }

    return false;
  }

  // Get token expiration time from JWT
  getTokenExpirationTime(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('Error parsing token expiration:', error);
      return null;
    }
  }

  // Check if token will expire within specified seconds
  isTokenExpiringSoon(secondsBeforeExpiry: number = 30): boolean {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) {
      return true; // If we can't parse the token, assume it's expired
    }

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const secondsUntilExpiry = timeUntilExpiry / 1000;

    const isExpiringSoon = secondsUntilExpiry <= secondsBeforeExpiry;
    
    // if (isExpiringSoon) {
    //   console.log(`🔍 Token expiration check: Token expires in ${secondsUntilExpiry.toFixed(1)}s (threshold: ${secondsBeforeExpiry}s) - REFRESH NEEDED`);
    // } else {
    //   console.log(`🔍 Token expiration check: Token expires in ${secondsUntilExpiry.toFixed(1)}s (threshold: ${secondsBeforeExpiry}s) - NO ACTION NEEDED`);
    // }

    return isExpiringSoon;
  }

  // Check if token is expired
  isTokenExpired(): boolean {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) return true;

    return Date.now() >= expirationTime;
  }

  // Get time until token expires in seconds
  getTimeUntilExpiry(): number {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) return 0;

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    return Math.max(0, timeUntilExpiry / 1000);
  }

  // Schedule automatic token refresh with specified buffer
  scheduleTokenRefresh(refreshCallback: () => void, secondsBeforeExpiry: number = 30): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Clear existing timer
    this.clearRefreshTimer();

    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) return;

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const refreshTime = timeUntilExpiry - (secondsBeforeExpiry * 1000);

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        refreshCallback();
        this.refreshTimer = null;
      }, refreshTime);
      
    } else {
      refreshCallback();
    }
  }

  // Clear the refresh timer
  clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Clear all authentication data
  clearAuth(): void {
    this.clearRefreshTimer();
    this.localStorage.removeitems('eol_access_token');
    this.localStorage.removeitems('eol_ref_token');
    this.localStorage.removeitems('eol_user_code');
    this.localStorage.removeitems('eolrefreshTokenInProgress');
  }

  // Store complete authentication data
  storeAuthData(authData: { 
    access_token: string; 
    refresh_token: string; 
    user_code: string; 
    expires_in?: number;
  }): void {
    this.setAccessToken(authData.access_token);
    this.setRefreshToken(authData.refresh_token);
    this.setUserCode(authData.user_code);
  }
} 